import { Subtitles, TableChart } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid2, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { useState } from "react";

const bgColor = "rgb(180, 180, 180)";
const size = 55;

export function makePolygon(lados: number, raio = 50): string {
  const pontos = [];
  for (let i = 0; i < lados; i++) {
    const angulo = (i * 2 * Math.PI) / lados - Math.PI / 2;
    const x = 50 + raio * Math.cos(angulo);
    const y = 50 + raio * Math.sin(angulo);
    pontos.push(`${x}% ${y}%`);
  }

  return `polygon(${pontos.join(", ")})`;
}

export function makeStar(
  pontas: number,
  raioExterno: number,
  raioInterno: number,
  centroX = 50,
  centroY = 50
): string {
  const pontos: string[] = [];
  const anguloIncremento = (2 * Math.PI) / pontas; // Ângulo entre cada ponta
  const deslocamentoAngular = -Math.PI / 2; // Rotação para começar no topo

  for (let i = 0; i < pontas * 2; i++) {
    const raio = i % 2 === 0 ? raioExterno : raioInterno; // Alterna entre raio externo e interno
    const angulo = i * anguloIncremento / 2 + deslocamentoAngular;
    const x = centroX + raio * Math.cos(angulo);
    const y = centroY + raio * Math.sin(angulo);
    pontos.push(`${x}% ${y}%`); // Usando % para CSS (ou sem % para SVG)
  }

  return `polygon(${pontos.join(", ")})`;
}

export const formasStyles: Record<string, React.CSSProperties> = {
  "Retângulo": {
    width: size,
    height: size,
    backgroundColor: bgColor,
  },
  "Retângulo Com Borda": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    borderRadius: "5px",
  },
  "Trapésio": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 0%, 100% 0%, 80% 100%, 20% 100%)',
  },
  "Losango": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  },
  "Pentagono": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makePolygon(5),
  },
  "Hexagono": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makePolygon(6),
  },
  "Heptágono": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makePolygon(7),
  },
  "Octógono": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makePolygon(8),
  },
  "Eneágono": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makePolygon(9),
  },
  "Estrela 1": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(4, size, size / 2),
  },
  "Estrela 2": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(5, size, size / 2),
  },
  "Estrela 3": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(6, size * 0.9, size / 1.5),
  },
  "Estrela 4": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(7, size * 0.9, size / 1.5),
  },
  "Estrela 5": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(8, size * 0.9, size / 1.5),
  },
  "Estrela 6": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(12, size * 0.9, size / 1.3),
  },
  "Estrela 7": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(15, size * 0.9, size / 1.3),
  },
  "Estrela 8": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: makeStar(24, size * 0.9, size / 1.3),
  },
  "Elipse": {
    width: size + 7,
    height: size,
    backgroundColor: bgColor,
    borderRadius: "100%",
  },
  "Círculo": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    borderRadius: "100%",
  },
  "Triângulo 1": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
  },
  "Triângulo 2": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 0%, 100% 0%, 50% 100%)',
  },
  "Triângulo 3": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 0%, 0% 100%, 100% 50%)',
  },
  "Triângulo 4": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(100% 0%, 100% 100%, 0% 50%)',
  },
  "Seta 1": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 20%, 0% 80%, 60% 80%, 60% 100%, 100% 50%, 60% 0%, 60% 20%)',
  },
  "Seta 2": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 50%, 40% 0%, 40% 20%, 100% 20%, 100% 80%, 40% 80%, 40% 100%)',
  },
  "Seta 3": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(50% 0%, 0% 40%, 20% 40%, 20% 100%, 80% 100%, 80% 40%, 100% 40%)',
  },
  "Seta 4": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(20% 0%, 80% 0%, 80% 60%, 100% 60%, 50% 100%, 0% 60%, 20% 60%)',
  },
  "Seta 5": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(50% 0%, 0% 40%, 20% 40%, 20% 60%, 0% 60%, 50% 100%, 100% 60%, 80% 60%, 80% 40%, 100% 40%)',
  },
  "Seta 6": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 50%, 40% 0%, 40% 20%, 60% 20%, 60% 0%, 100% 50%, 60% 100%, 60% 80%, 40% 80%, 40% 100%)',
  },
  "Indicador": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 0%, 70% 0%, 100% 50%, 70% 100%, 0% 100%)',
  },
  "Indicador 2": {
    width: size,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 0%, 70% 0%, 100% 50%, 70% 100%, 0% 100%, 30% 50%)',
  },
  "Indicador 3": {
    width: size + 5,
    height: size,
    backgroundColor: bgColor,
    clipPath: 'polygon(0% 0%, 100% 0%, 80% 50%, 100% 100%, 0% 100%, 20% 50%)',
  },
};

export interface SelectFormasProps {
  onDragLegend?: (event: React.DragEvent) => void;
  onDropLegend?: (event: React.DragEvent) => void;
  onDrag?: (event: React.DragEvent, type: string) => void;
  onDrop?: (event: React.DragEvent, type: string) => void;
  onDragTable?: (event: React.DragEvent, table: { rows: number; cols: number; title?: string; }) => void;
  onDropTable?: (event: React.DragEvent, table: { rows: number; cols: number; title?: string; }) => void;
}
export const SelectFormas: React.FC<SelectFormasProps> = ({
  onDrag,
  onDrop,
  onDragTable,
  onDropTable,
  onDragLegend,
  onDropLegend
}) => {

  const [tableDetails, setTableDetails] = useState<{ title?: string; rows: number; cols: number; }>({ rows: 2, cols: 5 });

  const handleChangeTitle = (value: string) => {
    let newValue: string | undefined = value;
    if (value.trim().length == 0) {
      newValue = undefined;
    }
    const newTable = {
      ...tableDetails,
      title: newValue,
    };
    setTableDetails(newTable);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeTExt = (key: string, value: any) => {
    let tryValue = value;
    if (typeof value === "string") {
      tryValue = value.replaceAll(",", ".").replaceAll(" ", "");

      const justNumber = /^\d+$/.test(tryValue);
      if (justNumber) {
        tryValue = parseFloat(tryValue);
      } else {
        tryValue = value;
      }
    }

    if (isNaN(tryValue)) {
      tryValue = value;
    }

    const newTable = {
      ...tableDetails,
      [key]: tryValue,
    };
    setTableDetails(newTable);
  };

  return <Box sx={{
    flex: 1,
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  }}>

    {/* Formas */}
    <Accordion disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography fontWeight="bold" fontSize="0.95rem">Formas</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={1}>
          <Grid2 container sx={{
            width: "100%",
            height: "100%",
          }}>
            {Object.keys(formasStyles).map(style => {
              return <Grid2
                draggable
                key={style}
                size={12 / 3}
                onDrag={(ev) => onDrag && onDrag(ev, style)}
                onDragEnd={(ev) => onDrop && onDrop(ev, style)}
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={formasStyles[style]} />
              </Grid2>;
            })}
          </Grid2>
        </Box>
      </AccordionDetails>
    </Accordion>

    {/* Tabelas */}
    <Accordion disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography fontWeight="bold" fontSize="0.95rem">Tabelas</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="row" gap={1} sx={{
          justifyContent: "center",
          alignItems: "center",
        }}>
          <Box sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label={"Título"}
              value={tableDetails.title}
              onChange={(e) => handleChangeTitle(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label={"Colunas"}
              value={tableDetails.cols}
              onChange={(e) => handleChangeTExt("cols", e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label={"Linhas"}
              value={tableDetails.rows}
              onChange={(e) => handleChangeTExt("rows", e.target.value)}
            />

          </Box>

          <Paper
            draggable
            onDrag={(ev) => onDragTable && onDragTable(ev, tableDetails)}
            onDragEnd={(ev) => onDropTable && onDropTable(ev, tableDetails)}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "50px",
              height: "50px",
              cursor: "pointer",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TableChart fontSize="large" />
          </Paper>
        </Box>
      </AccordionDetails>
    </Accordion>

    {/* Legendas */}
    <Accordion disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography fontWeight="bold" fontSize="0.95rem">Legendas</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="row" gap={1} sx={{
          justifyContent: "center",
          alignItems: "center",
        }}>
          <Tooltip title="Adicionar Legenda">
            <Paper
              draggable
              onDrag={(ev) => onDragLegend && onDragLegend(ev)}
              onDragEnd={(ev) => onDropLegend && onDropLegend(ev)}
              sx={{
                display: "flex",
                width: "50px",
                height: "50px",
                cursor: "pointer",
                alignItems: "center",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Subtitles fontSize="large" />
            </Paper>
          </Tooltip>
        </Box>
      </AccordionDetails>
    </Accordion>

  </Box>;
};



